/**
 * Runtime half of the disabled-pages switch (see disabled-pages.json).
 *
 * Bundled by build.js to /js/disabled-pages.js and loaded synchronously in
 * <head>, so it runs before the body is painted:
 *   - hides any element tagged data-collection="<disabled collection>"
 *     (e.g. the homepage entity cards) via an injected style rule;
 *   - renders links to /<disabled collection>/... as plain text, both in the
 *     initial HTML and in content rendered later by page scripts.
 *
 * nginx returns 404 for the same collections, so direct hits never reach a
 * page; this script only keeps the UI from advertising them.
 */

import { disabled } from './disabled-pages.json';

const DISABLED: readonly string[] = disabled;
// Generated pages use absolute https://us.elect.info/... links, so treat that
// host as same-site even when served from beta/preview hosts.
const SITE_HOST = 'us.elect.info';

function isDisabledHref(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return false;
  }
  if (url.host !== window.location.host && url.hostname !== SITE_HOST) {
    return false;
  }
  const collection = url.pathname.split('/')[1] || '';
  return DISABLED.includes(collection);
}

function unlinkDisabled(root: ParentNode): void {
  const anchors: HTMLAnchorElement[] = [];
  if (root instanceof HTMLAnchorElement) anchors.push(root);
  root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(a => anchors.push(a));

  for (const a of anchors) {
    const href = a.getAttribute('href');
    if (!href || !isDisabledHref(href)) continue;
    // Keep attributes (class, data-collection, ...) so styling and the
    // hide rule still apply; drop only the navigation-related ones.
    const span = document.createElement('span');
    for (const attr of Array.from(a.attributes)) {
      if (!['href', 'target', 'rel', 'download', 'ping'].includes(attr.name)) {
        span.setAttribute(attr.name, attr.value);
      }
    }
    span.classList.add('disabled-page-link');
    while (a.firstChild) span.appendChild(a.firstChild);
    a.replaceWith(span);
  }
}

function init(): void {
  if (DISABLED.length === 0) return;

  const style = document.createElement('style');
  style.textContent =
    DISABLED.map(c => `[data-collection="${c}"]`).join(',') + '{display:none !important}';
  document.head.appendChild(style);

  const start = (): void => {
    unlinkDisabled(document);
    new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          if (node instanceof Element) unlinkDisabled(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

init();
